import { KeyRound } from 'lucide-react'

import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'

import {
  isMorProcessor as _isMorProcessor,
  getMorPublishableKeyForEnv,
} from '@/lib/payment-constants'
import { Skeleton } from '@/components/ui/skeleton'
import { useApmCapabilities } from '@/lib/api/processors'
import { ApmInactiveOnAccountNotice } from './apm-inactive-on-account-notice'

function isMorProcessor(processorType: ApmProcessorType): boolean {
  return _isMorProcessor(processorType)
}

export function getMorPublishableKey(processorType: ApmProcessorType): string {
  return getMorPublishableKeyForEnv(processorType)
}

const STRIPE_APMS = [
  { key: 'klarna', label: 'Klarna', logoUrl: '/integrations/klarna.svg', badge: 'Buy Now Pay Later', defaultDescription: 'Pay in 4 interest-free payments', defaultCountries: ['US', 'GB', 'DE', 'AT', 'AU', 'BE', 'CA', 'CH', 'CZ', 'DK', 'ES', 'FI', 'FR', 'GR', 'IE', 'IT', 'NL', 'NO', 'NZ', 'PL', 'PT', 'RO', 'SE'], presentmentCurrencies: ['AUD', 'CAD', 'CHF', 'CZK', 'DKK', 'EUR', 'GBP', 'NOK', 'NZD', 'PLN', 'RON', 'SEK', 'USD'], docsUrl: 'https://docs.stripe.com/payments/klarna', recurring: true },
  { key: 'afterpay', label: 'Afterpay / Clearpay', logoUrl: '/integrations/afterpay.svg', badge: 'Buy Now Pay Later', defaultDescription: 'Buy now, pay later in 4 installments', defaultCountries: ['US', 'AU', 'GB', 'NZ', 'CA'], presentmentCurrencies: ['AUD', 'CAD', 'EUR', 'NZD', 'GBP', 'USD'], docsUrl: 'https://docs.stripe.com/payments/afterpay-clearpay', recurring: false },
  { key: 'ideal', label: 'iDEAL', logoUrl: '/integrations/ideal.svg', badge: 'Online Banking', defaultDescription: 'Pay with iDEAL bank transfer', defaultCountries: ['NL'], presentmentCurrencies: ['EUR'], docsUrl: 'https://docs.stripe.com/payments/ideal', recurring: true },
  { key: 'bancontact', label: 'Bancontact', logoUrl: '/integrations/bancontact.svg', badge: 'Online Banking', defaultDescription: 'Pay with your Bancontact card', defaultCountries: ['BE'], presentmentCurrencies: ['EUR'], docsUrl: 'https://docs.stripe.com/payments/bancontact', recurring: true },
  { key: 'twint', label: 'TWINT', logoUrl: '/integrations/twint.svg', badge: 'Mobile Payment', defaultDescription: 'Pay with TWINT mobile payment', defaultCountries: ['CH'], presentmentCurrencies: ['CHF'], docsUrl: 'https://docs.stripe.com/payments/twint', recurring: false },
  { key: 'blik', label: 'BLIK', logoUrl: '/integrations/blik.svg', badge: 'Mobile Banking', defaultDescription: 'Pay with your BLIK code', defaultCountries: ['PL'], presentmentCurrencies: ['PLN'], docsUrl: 'https://docs.stripe.com/payments/blik', recurring: false },
  { key: 'affirm', label: 'Affirm', logoUrl: '/integrations/affirm.svg', badge: 'Buy Now Pay Later', defaultDescription: 'Buy now, pay later with Affirm', defaultCountries: ['US', 'CA'], presentmentCurrencies: ['USD', 'CAD'], docsUrl: 'https://docs.stripe.com/payments/affirm', recurring: false },
] as const

// Express Checkout Element (ECE) methods — Stripe-only, rendered as one-click wallet buttons.
// 'klarna_express' is distinct from 'klarna' (redirect) to avoid config conflicts.
const STRIPE_EXPRESS_APMS = [
  { key: 'apple_pay', label: 'Apple Pay', logoUrl: '/integrations/apple-pay.svg' },
  { key: 'google_pay', label: 'Google Pay', logoUrl: '/integrations/google-pay.svg' },
  { key: 'link', label: 'Link by Stripe', logoUrl: '/integrations/link.png' },
  { key: 'klarna_express', label: 'Klarna (Express)', logoUrl: '/integrations/klarna.svg' },
] as const

const TAGADAPAY_ROUTER_APMS = [
  { key: 'ideal', label: 'iDEAL', logoUrl: '/integrations/ideal.svg', badge: 'Online Banking', defaultDescription: 'Pay with iDEAL bank transfer', defaultCountries: ['NL'], presentmentCurrencies: ['EUR'], docsUrl: 'https://docs.adyen.com/payment-methods/ideal', recurring: true },
  { key: 'klarna', label: 'Klarna', logoUrl: '/integrations/klarna.svg', badge: 'Buy Now Pay Later', defaultDescription: 'Pay in 4 interest-free payments', defaultCountries: ['AU', 'AT', 'BE', 'CA', 'CZ', 'DK', 'FI', 'FR', 'DE', 'GR', 'IE', 'IT', 'NL', 'NO', 'PL', 'PT', 'RO', 'ES', 'SE', 'CH', 'GB', 'US'], presentmentCurrencies: ['AUD', 'CAD', 'CHF', 'CZK', 'DKK', 'EUR', 'GBP', 'NOK', 'PLN', 'RON', 'SEK', 'USD'], docsUrl: 'https://docs.adyen.com/payment-methods/klarna', recurring: true },
  { key: 'paypal', label: 'PayPal', logoUrl: '/integrations/paypal.svg', badge: 'E-Wallet', defaultDescription: 'Pay securely with PayPal', defaultCountries: ['AU', 'BE', 'CA', 'CH', 'DE', 'DK', 'ES', 'FI', 'FR', 'GB', 'IE', 'IT', 'NL', 'NO', 'PL', 'PT', 'SE', 'US'], presentmentCurrencies: ['AUD', 'CAD', 'CHF', 'EUR', 'GBP', 'NOK', 'PLN', 'SEK', 'USD'], docsUrl: 'https://docs.adyen.com/payment-methods/paypal', recurring: true },
  { key: 'bancontact', label: 'Bancontact', logoUrl: '/integrations/bancontact.svg', badge: 'Online Banking', defaultDescription: 'Pay with your Bancontact card', defaultCountries: ['BE'], presentmentCurrencies: ['EUR'], docsUrl: 'https://docs.adyen.com/payment-methods/bancontact', recurring: true },
  { key: 'twint', label: 'TWINT', logoUrl: '/integrations/twint.svg', badge: 'Mobile Payment', defaultDescription: 'Pay with TWINT mobile payment', defaultCountries: ['CH'], presentmentCurrencies: ['CHF'], docsUrl: 'https://docs.adyen.com/payment-methods/twint', recurring: false },
  { key: 'blik', label: 'BLIK', logoUrl: '/integrations/blik.svg', badge: 'Mobile Banking', defaultDescription: 'Pay with your BLIK code', defaultCountries: ['PL'], presentmentCurrencies: ['PLN'], docsUrl: 'https://docs.adyen.com/payment-methods/blik', recurring: false },
  { key: 'eps', label: 'EPS', logoUrl: '/integrations/eps.svg', badge: 'Online Banking', defaultDescription: 'Pay with EPS online banking', defaultCountries: ['AT'], presentmentCurrencies: ['EUR'], docsUrl: 'https://docs.adyen.com/payment-methods/eps', recurring: false },
  { key: 'affirm', label: 'Affirm', logoUrl: '/integrations/affirm.svg', badge: 'Buy Now Pay Later', defaultDescription: 'Buy now, pay later with Affirm', defaultCountries: ['CA', 'GB', 'US'], presentmentCurrencies: ['CAD', 'GBP', 'USD'], docsUrl: 'https://docs.adyen.com/payment-methods/affirm', recurring: false },
  { key: 'afterpay', label: 'Afterpay', logoUrl: '/integrations/afterpay.svg', badge: 'Buy Now Pay Later', defaultDescription: 'Buy now, pay later in 4 installments', defaultCountries: ['AU', 'CA', 'NZ'], presentmentCurrencies: ['AUD', 'CAD', 'NZD'], docsUrl: 'https://docs.adyen.com/payment-methods/afterpaytouch', recurring: false },
] as const

const AIRWALLEX_APMS = [
  { key: 'klarna', label: 'Klarna', logoUrl: '/integrations/klarna.svg', badge: 'Buy Now Pay Later', defaultDescription: 'Pay in 4 interest-free payments', defaultCountries: ['AT', 'AU', 'BE', 'CA', 'CH', 'CZ', 'DE', 'DK', 'ES', 'FI', 'FR', 'GB', 'GR', 'IE', 'IT', 'NL', 'NO', 'NZ', 'PL', 'PT', 'SE', 'US'], presentmentCurrencies: ['AUD', 'CAD', 'CHF', 'CZK', 'DKK', 'EUR', 'GBP', 'NOK', 'NZD', 'PLN', 'SEK', 'USD'], docsUrl: 'https://www.airwallex.com/docs/payments/payment-methods/global/klarna', recurring: true },
  { key: 'afterpay', label: 'Afterpay / Clearpay', logoUrl: '/integrations/afterpay.svg', badge: 'Buy Now Pay Later', defaultDescription: 'Buy now, pay later in 4 installments', defaultCountries: ['AU', 'NZ', 'CA', 'US', 'GB'], presentmentCurrencies: ['AUD', 'NZD', 'CAD', 'USD', 'GBP'], docsUrl: 'https://www.airwallex.com/docs/payments/payment-methods/global/afterpay-clearpay', recurring: false },
  { key: 'paypal', label: 'PayPal', logoUrl: '/integrations/paypal.svg', badge: 'E-Wallet', defaultDescription: 'Pay securely with PayPal', defaultCountries: ['AU', 'US', 'GB', 'DE', 'FR', 'IT', 'ES', 'NL', 'BE', 'AT', 'CH', 'PL', 'PT', 'IE', 'SE', 'NO', 'FI', 'DK'], presentmentCurrencies: ['AUD', 'CAD', 'CHF', 'EUR', 'GBP', 'HKD', 'NZD', 'PLN', 'SEK', 'SGD', 'USD'], docsUrl: 'https://www.airwallex.com/docs/payments/payment-methods/global/paypal', recurring: true },
  { key: 'skrill', label: 'Skrill', logoUrl: '/integrations/skrill.svg', badge: 'E-Wallet', defaultDescription: 'Pay securely with Skrill', defaultCountries: ['US', 'GB', 'DE', 'FR', 'IT', 'ES', 'NL', 'BE', 'AT', 'CH', 'PL', 'PT', 'IE', 'SE', 'NO', 'FI', 'DK'], presentmentCurrencies: ['EUR', 'GBP', 'USD'], docsUrl: 'https://www.airwallex.com/docs/payments/payment-methods/global/skrill', recurring: false },
  { key: 'bancontact', label: 'Bancontact', logoUrl: '/integrations/bancontact.svg', badge: 'Bank Transfer', defaultDescription: 'Pay with your Bancontact card', defaultCountries: ['BE'], presentmentCurrencies: ['EUR'], docsUrl: 'https://www.airwallex.com/docs/payments/payment-methods/emea/bancontact', recurring: false },
  { key: 'ideal', label: 'iDEAL', logoUrl: '/integrations/ideal.svg', badge: 'Online Banking', defaultDescription: 'Pay with iDEAL bank transfer', defaultCountries: ['NL'], presentmentCurrencies: ['EUR'], docsUrl: 'https://www.airwallex.com/docs/payments/payment-methods/emea/ideal', recurring: false },
  { key: 'twint', label: 'TWINT', logoUrl: '/integrations/twint.svg', badge: 'Mobile Payment', defaultDescription: 'Pay with TWINT mobile payment', defaultCountries: ['CH'], presentmentCurrencies: ['CHF'], docsUrl: 'https://www.airwallex.com/docs/payments/payment-methods/emea/twint-beta', recurring: false },
] as const

type ApmDefinition = {
  readonly key: string
  readonly label: string
  readonly logoUrl: string
  readonly badge: string
  readonly defaultDescription: string
  readonly defaultCountries: readonly string[]
  readonly presentmentCurrencies: readonly string[]
  readonly docsUrl: string
  readonly recurring: boolean
}

type ExpressApmDefinition = {
  readonly key: string
  readonly label: string
  readonly logoUrl: string
}

export interface ApmMethodState {
  enabled: boolean
  label: string
  logoUrl: string
  isExpress?: boolean
  description?: string
  supportedCountries?: string[]
  presentmentCurrencies?: string[]
  recurring?: boolean
}

export type ApmSettings = Record<string, ApmMethodState>

type ApmProcessorType = 'stripe' | 'tagadapay' | 'tagadapay-router' | 'airwallex' | 'efipayments' | 'merchantscale' | 'bakolapay'

/**
 * How a method is PRESENTED — label, logo, countries, docs link — per acquirer
 * family. It deliberately says nothing about whether the method can be charged:
 * that is capability, it belongs to the charge path, and the server serves it
 * through `processors.apmCapabilities` (#TAG-2506). The CRM used to answer both
 * questions from these lists, which is how iDEAL stayed togglable on MoR-Stripe
 * for four months after it was removed from the charge path.
 *
 * The router delegates to one acquirer per linked TagadaPay account, so a
 * Stripe-backed router is presented with Stripe's copy (its docs links point at
 * docs.stripe.com), not Adyen's — the same split `getExpressApmList` makes.
 */
export function getApmList(
  processorType: ApmProcessorType,
  payfacProvider?: string,
): readonly ApmDefinition[] {
  if (processorType === 'airwallex') return AIRWALLEX_APMS
  if (processorType === 'tagadapay-router') {
    return payfacProvider === 'stripe' ? STRIPE_APMS : TAGADAPAY_ROUTER_APMS
  }
  return STRIPE_APMS // stripe, tagadapay, efipayments, merchantscale, bakolapay
}

export type ApmListState =
  | { readonly status: 'pending' }
  | { readonly status: 'ready'; readonly apms: readonly ApmDefinition[] }

/**
 * What the standard APM section shows, for one state of the capability query.
 *
 * Two different absences used to collapse into a single `undefined` here, and
 * answering both with the unfiltered list is what made iDEAL appear on screen
 * and then disappear:
 *
 * - **in flight** — there is no answer yet, so there is nothing to show yet.
 *   A method the answer may remove must not be rendered, and above all not
 *   rendered with a live toggle: a merchant who reaches it in that window can
 *   enable a method that is about to vanish under their cursor.
 * - **settled, no capability for this type** — the API is older than this SPA,
 *   or the router's delegate could not be resolved. Nothing is filtered, which
 *   is what this screen has always done; hiding every toggle because the server
 *   stayed silent would read as data loss.
 *
 * `inactiveOnAccount` (what the processor's own Stripe account has not
 * activated, #TAG-3194) is dropped whatever the type map says.
 */
export function resolveApmList(
  apms: readonly ApmDefinition[],
  supported: readonly string[] | undefined,
  isCapabilityPending: boolean,
  inactiveOnAccount: readonly string[] = [],
): ApmListState {
  if (isCapabilityPending) return { status: 'pending' }
  const offered = apms.filter((apm) => !inactiveOnAccount.includes(apm.key))
  if (!supported) return { status: 'ready', apms: offered }
  return { status: 'ready', apms: offered.filter((apm) => supported.includes(apm.key)) }
}

function getExpressApmList(
  processorType: ApmProcessorType,
  payfacProvider?: string,
): readonly ExpressApmDefinition[] {
  if (processorType === 'airwallex') return [] // Airwallex has no ECE support
  if (processorType === 'tagadapay-router') {
    // The router delegates to a single acquirer per linked TagadaPay account.
    // A Stripe-backed router supports the Express Checkout Element (Apple/Google
    // Pay) exactly like the old `tagadapay` MoR processor; Adyen/Tilled/Nuvei/
    // Checkout/Airwallex-backed routers do not.
    return payfacProvider === 'stripe' ? STRIPE_EXPRESS_APMS : []
  }
  return STRIPE_EXPRESS_APMS
}

export function buildDefaultApmSettings(
  processorType: ApmProcessorType,
  payfacProvider?: string,
): ApmSettings {
  const apms = getApmList(processorType, payfacProvider)
  const expressApms = getExpressApmList(processorType, payfacProvider)
  // A Stripe-backed router mirrors the old `tagadapay` MoR default, where the
  // express wallets (Apple/Google Pay) ship enabled out of the box.
  const expressDefaultEnabled = processorType === 'tagadapay-router' && payfacProvider === 'stripe'
  const settings: ApmSettings = {}
  for (const apm of apms) {
    settings[apm.key] = {
      enabled: false,
      label: apm.label,
      description: apm.defaultDescription,
      supportedCountries: [...apm.defaultCountries],
      presentmentCurrencies: [...apm.presentmentCurrencies],
      logoUrl: apm.logoUrl,
      recurring: apm.recurring,
    }
  }
  for (const apm of expressApms) {
    settings[apm.key] = {
      enabled: expressDefaultEnabled,
      label: apm.label,
      logoUrl: apm.logoUrl,
      isExpress: true,
    }
  }
  return settings
}

export function initApmSettingsFromOptions(
  processorType: ApmProcessorType,
  apmSettings?: Record<string, Record<string, unknown>>,
  payfacProvider?: string,
): ApmSettings {
  const defaults = buildDefaultApmSettings(processorType, payfacProvider)
  if (!apmSettings) return defaults

  const expressKeys = new Set(getExpressApmList(processorType, payfacProvider).map((a) => a.key))
  const allStandardApms = getApmList(processorType, payfacProvider)
  const allExpressApms = getExpressApmList(processorType, payfacProvider)

  for (const apm of allStandardApms) {
    const saved = apmSettings[apm.key]
    if (saved) {
      defaults[apm.key] = {
        enabled: (saved.enabled as boolean) ?? false,
        label: (saved.label as string) ?? apm.label,
        description: (saved.description as string) ?? apm.defaultDescription,
        supportedCountries: (saved.supportedCountries as string[]) ?? [...apm.defaultCountries],
        presentmentCurrencies: (saved.presentmentCurrencies as string[]) ?? [...apm.presentmentCurrencies],
        logoUrl: (saved.logoUrl as string) ?? apm.logoUrl,
        recurring: (saved.recurring as boolean) ?? apm.recurring,
      }
    }
  }

  for (const apm of allExpressApms) {
    const saved = apmSettings[apm.key]
    if (saved) {
      defaults[apm.key] = {
        enabled: (saved.enabled as boolean) ?? false,
        label: (saved.label as string) ?? apm.label,
        logoUrl: (saved.logoUrl as string) ?? apm.logoUrl,
        isExpress: true,
      }
    }
  }

  // Stamp isExpress on any express key that was saved without the flag (old records)
  for (const key of expressKeys) {
    if (defaults[key]) defaults[key].isExpress = true
  }

  // Whatever is stored under a key this screen does not present is carried
  // through as is — never dropped. The edit pages replace `options` wholesale
  // with this map, so anything left out here is deleted on save: the methods
  // payment-stack mirrors onto a Checkout or Nuvei router (multibanco, sepa…)
  // and, since a Stripe-backed router is presented with Stripe's list, its
  // stored `paypal` / `eps`. Whether such a key may stay enabled is decided
  // server-side by `disableUnchargeableApms`, which disables and never deletes
  // (#TAG-2506). Presenting nothing for it is fine; erasing it is not.
  for (const [key, saved] of Object.entries(apmSettings)) {
    if (defaults[key]) continue
    const stored = saved as Partial<ApmMethodState>
    defaults[key] = {
      ...stored,
      enabled: stored.enabled === true,
      label: stored.label ?? key,
      logoUrl: stored.logoUrl ?? '',
    }
  }

  return defaults
}

interface ProcessorApmSettingsProps {
  processorType: ApmProcessorType
  /** Saved processor, when there is one: its own Stripe account is checked for what it has activated. */
  processorId?: string
  /** Underlying acquirer of the linked account — gates Stripe-only express checkout for the router. */
  payfacProvider?: string
  value: ApmSettings
  onChange: (settings: ApmSettings) => void
  expressPublishableKey?: string
  onExpressPublishableKeyChange?: (key: string) => void
  disabled?: boolean
}

export function ProcessorApmSettings({
  processorType,
  processorId,
  payfacProvider,
  value,
  onChange,
  expressPublishableKey = '',
  onExpressPublishableKeyChange,
  disabled = false,
}: ProcessorApmSettingsProps) {
  // Capability comes from the server, presentation from the lists above. The
  // router is resolved against the TagadaPay account selected in the form —
  // which is why the delegate map is served rather than assumed here.
  const { data: capabilities, isPending: isCapabilityPending } = useApmCapabilities(processorId)
  const effectiveType =
    processorType === 'tagadapay-router'
      ? (capabilities?.routerDelegate[payfacProvider ?? ''] ?? processorType)
      : processorType
  const supportedMethods = capabilities?.byEffectiveType[effectiveType]
  const inactiveOnAccount = capabilities?.inactiveOnAccount ?? []

  const presentedApms = getApmList(processorType, payfacProvider)
  const apmList = resolveApmList(presentedApms, supportedMethods, isCapabilityPending, inactiveOnAccount)
  const expressApms = getExpressApmList(processorType, payfacProvider)
  // An Adyen-backed router's APM settings are owned by payment-stack: the ops
  // dashboard enables methods on the provider and mirrors the result here on a
  // FULL RECONCILE (see payment-stack `providers/adyen/apm-sync.ts`). Anything
  // edited here would be silently overwritten on the next sync, so show the
  // mirrored state read-only and send operators to the dashboard.
  //
  // Scoped to Adyen deliberately — `syncRouterApmSettings` no-ops for every
  // other payfac provider, so a Stripe-backed router has no mirror and this tab
  // remains its only configuration surface.
  const isMirroredFromOps = processorType === 'tagadapay-router' && payfacProvider === 'adyen'
  const readOnly = disabled || isMirroredFromOps
  // A Stripe-backed router uses the shared TagadaPay MoR platform key (autofilled
  // by the edit page), so treat it like a MoR: no manual publishable-key entry.
  const isMor =
    isMorProcessor(processorType) || (processorType === 'tagadapay-router' && payfacProvider === 'stripe')

  const updateApm = (key: string, updates: Partial<ApmMethodState>) => {
    onChange({
      ...value,
      [key]: { ...value[key], ...updates },
    })
  }

  const renderApmCard = (apm: ApmDefinition) => {
    const state = value[apm.key]
    if (!state) return null

    return (
      <Card key={apm.key}>
        <CardHeader className="py-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <CardTitle className="flex items-center gap-2 text-base">
                <img src={apm.logoUrl} alt={apm.label} className="h-6 w-auto object-contain" />
                {apm.label}
                <Badge variant="outline" className="text-xs">
                  {apm.badge}
                </Badge>
              </CardTitle>
              <CardDescription className="text-xs">
                {(state.supportedCountries?.length ?? 0)} countries · {apm.presentmentCurrencies.join(', ')} · Recurring: {apm.recurring ? 'Yes' : 'No'}
              </CardDescription>
            </div>
            <Switch
              checked={state.enabled}
              onCheckedChange={(enabled) => updateApm(apm.key, { enabled })}
              disabled={readOnly}
            />
          </div>
        </CardHeader>
        {state.enabled && (
          <CardContent className="space-y-4 pt-0">
            <div className="space-y-2">
              <Label htmlFor={`apm-${apm.key}-description`}>Checkout Description</Label>
              <Input
                id={`apm-${apm.key}-description`}
                value={state.description}
                onChange={(e) => updateApm(apm.key, { description: e.target.value })}
                placeholder={apm.defaultDescription}
                disabled={readOnly}
              />
              <p className="text-xs text-muted-foreground">
                Displayed to customers at checkout when this payment method is selected
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Supported Countries</Label>
              <div className="flex flex-wrap gap-1">
                {state.supportedCountries?.map((country) => (
                  <Badge key={country} variant="secondary" className="text-xs">
                    {country}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Presentment currencies</Label>
              <p className="text-xs text-muted-foreground">
                Checkout must use one of these currencies for this method to be available.
              </p>
              <div className="flex flex-wrap gap-1">
                {apm.presentmentCurrencies.map((currency) => (
                  <Badge key={currency} variant="outline" className="text-xs font-mono">
                    {currency}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Recurring / Subscriptions</Label>
              <Badge variant={apm.recurring ? 'default' : 'outline'} className="text-xs">
                {apm.recurring ? 'Supported' : 'Not supported'}
              </Badge>
            </div>
          </CardContent>
        )}
      </Card>
    )
  }

  const renderExpressApmCard = (apm: ExpressApmDefinition) => {
    const state = value[apm.key]
    if (!state) return null

    return (
      <Card key={apm.key}>
        <CardHeader className="py-4">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <img src={apm.logoUrl} alt={apm.label} className="h-6 w-auto object-contain" />
              {apm.label}
            </CardTitle>
            <Switch
              checked={state.enabled}
              onCheckedChange={(enabled) => updateApm(apm.key, { enabled })}
              disabled={readOnly}
            />
          </div>
        </CardHeader>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {isMirroredFromOps && (
        <Card className="border-dashed">
          <CardHeader className="py-4">
            <CardTitle className="text-sm font-medium">Managed in the TagadaPay dashboard</CardTitle>
            <CardDescription className="text-xs">
              These methods mirror what is enabled on the merchant&apos;s payment provider. They are
              synced automatically and shown here read-only — enable, disable or edit them from the
              TagadaPay dashboard (Ops → Payment Methods).
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      <ApmInactiveOnAccountNotice apms={presentedApms} capabilities={capabilities} />

      <div className="space-y-4">
        {apmList.status === 'pending' ? (
          Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-20 w-full" />)
        ) : (
          apmList.apms.map(renderApmCard)
        )}
      </div>

      {expressApms.length > 0 && (
        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-semibold">Express Checkout Methods</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              One-click wallet buttons. The element automatically shows only the methods available on the customer&apos;s device and browser.
            </p>
          </div>

          {/* Publishable key for ECE — MoRs autofill, plain Stripe needs manual entry */}
          {!isMor && (
            <Card>
              <CardHeader className="py-4">
                <CardTitle className="flex items-center gap-2 text-sm font-medium">
                  <KeyRound className="h-4 w-4" />
                  Publishable Key
                </CardTitle>
                <CardDescription className="text-xs">
                  Required to initialise the express checkout buttons.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-2">
                  <Input
                    id="express-publishable-key"
                    value={expressPublishableKey}
                    onChange={(e) => onExpressPublishableKeyChange?.(e.target.value)}
                    placeholder="pk_live_..."
                    disabled={readOnly}
                  />
                  <p className="text-xs text-muted-foreground">
                    Find this in your Stripe Dashboard under Developers → API keys.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {expressApms.map(renderExpressApmCard)}
        </div>
      )}
    </div>
  )
}
